import { supabase } from '../lib/supabase';

const EDGE_FUNCTION_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-transactional-emails`;
const SMS_EDGE_FUNCTION_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-sms-confirmation`;

/**
 * Calls the Resend edge function to send a transactional email.
 * Fails silently so it never blocks the main user flow.
 */
const sendEmail = async (type, data) => {
  try {
    const { data: sessionData } = await supabase?.auth?.getSession();
    const accessToken = sessionData?.session?.access_token;

    const headers = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ type, data }),
    });

    if (!response?.ok) {
      const errorData = await response?.json()?.catch(() => ({}));
      console.warn(`[EmailNotification] Failed to send "${type}" email:`, errorData?.error || response?.statusText);
      return { success: false, error: errorData?.error };
    }

    const result = await response?.json();
    return { success: true, data: result };
  } catch (err) {
    // Never throw — email sending must not break the main flow
    console.warn(`[EmailNotification] Error sending "${type}" email:`, err?.message);
    return { success: false, error: err?.message };
  }
};

/**
 * Calls the SMS edge function (Twilio) to send a confirmation SMS.
 * Fails silently — never blocks the main user flow.
 * Only fires when a client_phone is present in the data payload.
 */
const sendSms = async (type, data) => {
  try {
    // Skip silently if no phone number provided
    if (!data?.client_phone && !data?.phone) return { success: false, skipped: true, reason: 'no_phone' };

    const { data: sessionData } = await supabase?.auth?.getSession();
    const accessToken = sessionData?.session?.access_token;

    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    const response = await fetch(SMS_EDGE_FUNCTION_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ type, data }),
    });

    if (!response?.ok) {
      const errorData = await response?.json()?.catch(() => ({}));
      console.warn(`[SMSNotification] Failed to send "${type}" SMS:`, errorData?.error || response?.statusText);
      return { success: false, error: errorData?.error };
    }

    const result = await response?.json();
    return { success: true, data: result };
  } catch (err) {
    console.warn(`[SMSNotification] Error sending "${type}" SMS:`, err?.message);
    return { success: false, error: err?.message };
  }
};

/**
 * Send appointment confirmation email (consultation booking hub).
 * @param {object} params
 * @param {string} params.client_name
 * @param {string} params.client_email
 * @param {string} [params.client_phone]  - triggers SMS fallback if provided
 * @param {string} params.appointment_type
 * @param {string} params.scheduled_date  - ISO date string
 * @param {string} [params.scheduled_time]
 * @param {number} [params.duration_minutes]
 * @param {string} [params.location]
 * @param {string} [params.notary_name]
 * @param {string} [params.consultant_name]
 */
export const sendAppointmentConfirmation = (params) => {
  sendSms('appointment_confirmation', params); // fire-and-forget SMS fallback
  return sendEmail('appointment_confirmation', params);
};

/**
 * Send property inquiry acknowledgement email.
 * @param {object} params
 * @param {string} [params.client_name]
 * @param {string} params.client_email
 * @param {string} [params.client_phone]  - triggers SMS fallback if provided
 * @param {string} [params.property_title]
 * @param {string} [params.property_type]
 * @param {string} [params.message]
 * @param {string} [params.preferred_contact_time]
 */
export const sendPropertyInquiryConfirmation = (params) => {
  sendSms('property_inquiry', params); // fire-and-forget SMS fallback
  return sendEmail('property_inquiry', params);
};

/**
 * Send notary appointment confirmation email.
 * @param {object} params
 * @param {string} params.client_name
 * @param {string} params.client_email
 * @param {string} [params.client_phone]  - triggers SMS fallback if provided
 * @param {string} [params.notary_name]
 * @param {string} [params.notary_office]
 * @param {string} params.appointment_type
 * @param {string} params.scheduled_date  - ISO date string
 * @param {string} [params.scheduled_time]
 * @param {number} [params.duration_minutes]
 * @param {string} [params.property_reference]
 */
export const sendNotaryAppointmentConfirmation = (params) => {
  sendSms('notary_appointment', params); // fire-and-forget SMS fallback
  return sendEmail('notary_appointment', params);
};

/**
 * Send appointment rescheduled notification (email + SMS).
 * @param {object} params
 * @param {string} params.client_name
 * @param {string} params.client_email
 * @param {string} [params.client_phone]
 * @param {string} params.appointment_type
 * @param {string} params.new_date
 * @param {string} [params.new_time]
 * @param {string} [params.old_date]
 * @param {string} [params.notary_name]
 * @param {string} [params.consultant_name]
 * @param {string} [params.location]
 */
export const sendAppointmentRescheduledNotification = (params) => {
  sendSms('appointment_rescheduled', params); // fire-and-forget SMS
  return sendEmail('appointment_rescheduled', params);
};

/**
 * Send appointment status update notification (email + SMS).
 * @param {object} params
 * @param {string} params.client_name
 * @param {string} params.client_email
 * @param {string} [params.client_phone]
 * @param {string} params.appointment_type
 * @param {string} params.scheduled_date
 * @param {string} [params.scheduled_time]
 * @param {'confirmed'|'cancelled'|'completed'|'rescheduled'} params.new_status
 * @param {string} [params.notary_name]
 * @param {string} [params.location]
 * @param {string} [params.admin_note]
 */
export const sendAppointmentStatusUpdateNotification = (params) => {
  sendSms('appointment_status_update', params); // fire-and-forget SMS
  return sendEmail('appointment_status_update', params);
};

/**
 * Send inquiry response notification (email + SMS).
 * @param {object} params
 * @param {string} params.client_name
 * @param {string} params.client_email
 * @param {string} [params.client_phone]
 * @param {string} params.property_title
 * @param {string} [params.property_type]
 * @param {string} [params.inquiry_message]
 * @param {string} params.response_message
 * @param {string} [params.agent_name]
 */
export const sendInquiryResponseNotification = (params) => {
  sendSms('inquiry_response', params); // fire-and-forget SMS
  return sendEmail('inquiry_response', params);
};

/**
 * Get or create the digest preference for the current user.
 * Returns { frequency, appointmentFrequency, inquiryFrequency, notaryFrequency, preferredHour, preferredDayOfWeek }
 */
export const getDigestPreferences = async (userId, userEmail) => {
  try {
    const { data, error } = await supabase
      ?.from('email_digest_preferences')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.maybeSingle();

    if (error) throw error;

    if (data) {
      return {
        success: true,
        preferences: {
          id: data?.id,
          frequency: data?.frequency || 'immediate',
          appointmentFrequency: data?.appointment_frequency || null,
          inquiryFrequency: data?.inquiry_frequency || null,
          notaryFrequency: data?.notary_frequency || null,
          preferredHour: data?.preferred_hour ?? 8,
          preferredDayOfWeek: data?.preferred_day_of_week ?? 1,
        },
      };
    }

    // Return defaults if no preference row yet
    return {
      success: true,
      preferences: {
        id: null,
        frequency: 'immediate',
        appointmentFrequency: null,
        inquiryFrequency: null,
        notaryFrequency: null,
        preferredHour: 8,
        preferredDayOfWeek: 1,
      },
    };
  } catch (err) {
    console.warn('[DigestPreferences] Error fetching preferences:', err?.message);
    return { success: false, error: err?.message };
  }
};

/**
 * Save (upsert) digest preferences for the current user.
 */
export const saveDigestPreferences = async (userId, userEmail, prefs) => {
  try {
    const payload = {
      user_id: userId,
      email: userEmail,
      frequency: prefs?.frequency || 'immediate',
      appointment_frequency: prefs?.appointmentFrequency || null,
      inquiry_frequency: prefs?.inquiryFrequency || null,
      notary_frequency: prefs?.notaryFrequency || null,
      preferred_hour: prefs?.preferredHour ?? 8,
      preferred_day_of_week: prefs?.preferredDayOfWeek ?? 1,
    };

    const { error } = await supabase
      ?.from('email_digest_preferences')
      ?.upsert(payload, { onConflict: 'user_id' });

    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.warn('[DigestPreferences] Error saving preferences:', err?.message);
    return { success: false, error: err?.message };
  }
};

/**
 * Queue a notification for digest delivery instead of sending immediately.
 * Called when user's preference for a given type is 'daily' or 'weekly'.
 */
export const queueForDigest = async (userId, userEmail, notificationType, notificationData, frequency) => {
  try {
    const now = new Date();
    let scheduledFor;

    if (frequency === 'daily') {
      // Schedule for next 8 AM
      const next = new Date(now);
      next?.setDate(next?.getDate() + 1);
      next?.setHours(8, 0, 0, 0);
      scheduledFor = next?.toISOString();
    } else if (frequency === 'weekly') {
      // Schedule for next Monday 8 AM
      const next = new Date(now);
      const daysUntilMonday = (8 - next?.getDay()) % 7 || 7;
      next?.setDate(next?.getDate() + daysUntilMonday);
      next?.setHours(8, 0, 0, 0);
      scheduledFor = next?.toISOString();
    } else {
      scheduledFor = now?.toISOString();
    }

    const { error } = await supabase?.from('email_digest_queue')?.insert({
      user_id: userId,
      email: userEmail,
      notification_type: notificationType,
      notification_data: notificationData,
      frequency,
      status: 'pending',
      scheduled_for: scheduledFor,
    });

    if (error) throw error;
    return { success: true, scheduledFor };
  } catch (err) {
    console.warn('[DigestQueue] Error queuing notification:', err?.message);
    return { success: false, error: err?.message };
  }
};

/**
 * Resolve the effective frequency for a given notification type.
 * Per-type override takes precedence over global frequency.
 */
export const resolveFrequency = (prefs, notificationType) => {
  if (!prefs) return 'immediate';
  const typeMap = {
    appointment_confirmation: prefs?.appointmentFrequency,
    property_inquiry: prefs?.inquiryFrequency,
    notary_appointment: prefs?.notaryFrequency,
  };
  return typeMap?.[notificationType] || prefs?.frequency || 'immediate';
};

/**
 * Smart send: checks user digest preferences and either sends immediately
 * or queues the notification for batch delivery.
 * @param {string} type - 'appointment_confirmation' | 'property_inquiry' | 'notary_appointment'
 * @param {object} data - email data payload
 * @param {string} userId - authenticated user id
 * @param {string} userEmail - recipient email
 */
export const smartSendEmail = async (type, data, userId, userEmail) => {
  try {
    if (!userId) {
      // No user context — send immediately (anonymous flows)
      return sendEmail(type, data);
    }

    const { preferences } = await getDigestPreferences(userId, userEmail);
    const frequency = resolveFrequency(preferences, type);

    if (frequency === 'immediate' || !frequency) {
      return sendEmail(type, data);
    }

    // Queue for digest
    return queueForDigest(userId, userEmail, type, data, frequency);
  } catch (err) {
    console.warn('[SmartSend] Falling back to immediate send:', err?.message);
    return sendEmail(type, data);
  }
};

export default {
  sendAppointmentConfirmation,
  sendPropertyInquiryConfirmation,
  sendNotaryAppointmentConfirmation,
  sendAppointmentRescheduledNotification,
  sendAppointmentStatusUpdateNotification,
  sendInquiryResponseNotification,
};
